import React from 'react';
import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

const heatPulse = keyframes`
  0%, 100% {
    opacity: 0.25;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.08);
  }
`;

const gridPulse = keyframes`
  0%, 100% { opacity: 0.08; }
  50% { opacity: 0.15; }
`;

const lineFlow = keyframes`
  0% { stroke-dashoffset: 2000; }
  100% { stroke-dashoffset: 0; }
`;

const dataPointPop = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.4); opacity: 0.8; }
`;

const bubbleFloat = keyframes`
  0% { transform: translateY(100vh) scale(0.5); opacity: 0; }
  10% { opacity: 0.5; }
  90% { opacity: 0.4; }
  100% { transform: translateY(-100px) scale(1); opacity: 0; }
`;

const bubbles = [
  { size: 18, left: '8%', delay: 0, duration: 16 },
  { size: 28, left: '22%', delay: 4, duration: 20 },
  { size: 14, left: '38%', delay: 2, duration: 14 },
  { size: 24, left: '52%', delay: 6, duration: 18 },
  { size: 20, left: '68%', delay: 3, duration: 15 },
  { size: 16, left: '82%', delay: 8, duration: 17 },
  { size: 22, left: '15%', delay: 10, duration: 19 },
  { size: 26, left: '75%', delay: 5, duration: 21 },
];

const dataPoints = [
  { x: '12%', y: '30%', delay: 0 },
  { x: '28%', y: '50%', delay: 1.2 },
  { x: '42%', y: '35%', delay: 0.6 },
  { x: '58%', y: '60%', delay: 1.8 },
  { x: '72%', y: '40%', delay: 0.9 },
  { x: '88%', y: '55%', delay: 1.5 },
];

export const HeatZoneBackground = () => {
  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      pointerEvents="none"
      zIndex={0}
      overflow="hidden"
      bg="#0f172a"
    >
      {/* Deep Earth Gradient - Bottom */}
      <Box
        position="absolute"
        w="600px"
        h="600px"
        borderRadius="50%"
        bgGradient="radial(rgba(180, 83, 9, 0.35) 0%, transparent 70%)"
        filter="blur(100px)"
        bottom="-250px"
        left="-150px"
        animation={`${heatPulse} 10s ease-in-out infinite`}
      />

      {/* Geothermal Core - Bottom Right */}
      <Box
        position="absolute"
        w="500px"
        h="500px"
        borderRadius="50%"
        bgGradient="radial(rgba(220, 38, 38, 0.3) 0%, transparent 70%)"
        filter="blur(100px)"
        bottom="-200px"
        right="5%"
        animation={`${heatPulse} 10s ease-in-out infinite 3s`}
      />

      {/* Cool Zone - Upper */}
      <Box
        position="absolute"
        w="400px"
        h="400px"
        borderRadius="50%"
        bgGradient="radial(rgba(59, 130, 246, 0.25) 0%, transparent 70%)"
        filter="blur(80px)"
        top="-100px"
        right="20%"
        animation={`${heatPulse} 12s ease-in-out infinite 5s`}
      />

      {/* Subtle Grid */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        animation={`${gridPulse} 8s ease-in-out infinite`}
        sx={{
          backgroundImage: `
            linear-gradient(rgba(148, 163, 184, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148, 163, 184, 0.08) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Flowing Well Path Lines */}
      <Box position="absolute" top="15%" left={0} right={0} opacity={0.5}>
        <svg width="100%" height="250" viewBox="0 0 1200 250" preserveAspectRatio="none">
          <defs>
            <linearGradient id="wellGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.7" />
              <stop offset="50%" stopColor="#dc2626" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#b45309" stopOpacity="0.7" />
            </linearGradient>
            <linearGradient id="wellGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.5" />
            </linearGradient>
            <radialGradient id="pulseGlow1">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="40%" stopColor="#f97316" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="pulseGlow2">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="40%" stopColor="#3b82f6" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
            </radialGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Well path 1 - represents drilling trajectory */}
          <path
            id="wellPath1"
            d="M0 180 Q150 120 300 140 T600 100 T900 150 T1200 90"
            fill="none"
            stroke="url(#wellGradient1)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="2000"
            style={{ animation: `${lineFlow} 10s linear infinite` }}
          />

          {/* Well path 2 - cooler formation */}
          <path
            id="wellPath2"
            d="M0 80 Q200 130 400 70 T800 120 T1200 60"
            fill="none"
            stroke="url(#wellGradient2)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="2000"
            style={{ animation: `${lineFlow} 14s linear infinite` }}
          />

          {/* Pulse traveling along well path 1 */}
          <circle r="8" fill="url(#pulseGlow1)" filter="url(#glow)">
            <animateMotion dur="5s" repeatCount="indefinite">
              <mpath href="#wellPath1" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="5s" repeatCount="indefinite" />
          </circle>

          {/* Second pulse on path 1 */}
          <circle r="6" fill="url(#pulseGlow1)" filter="url(#glow)">
            <animateMotion dur="5s" repeatCount="indefinite" begin="2.5s">
              <mpath href="#wellPath1" />
            </animateMotion>
            <animate attributeName="opacity" values="0;0.8;0.8;0" keyTimes="0;0.1;0.9;1" dur="5s" repeatCount="indefinite" begin="2.5s" />
          </circle>

          {/* Pulse on path 2 */}
          <circle r="6" fill="url(#pulseGlow2)" filter="url(#glow)">
            <animateMotion dur="7s" repeatCount="indefinite" begin="1s">
              <mpath href="#wellPath2" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="7s" repeatCount="indefinite" begin="1s" />
          </circle>
        </svg>
      </Box>

      {/* Bottom flow lines - deeper formation */}
      <Box position="absolute" bottom="20%" left={0} right={0} opacity={0.4}>
        <svg width="100%" height="180" viewBox="0 0 1200 180" preserveAspectRatio="none">
          <defs>
            <linearGradient id="deepGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#dc2626" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#b45309" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0.6" />
            </linearGradient>
            <radialGradient id="pulseGlow3">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="30%" stopColor="#f59e0b" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
            </radialGradient>
          </defs>

          <path
            id="deepPath"
            d="M0 100 Q250 50 500 110 T1000 70 T1200 120"
            fill="none"
            stroke="url(#deepGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="2000"
            style={{ animation: `${lineFlow} 8s linear infinite` }}
          />

          <circle r="10" fill="url(#pulseGlow3)" filter="url(#glow)">
            <animateMotion dur="8s" repeatCount="indefinite">
              <mpath href="#deepPath" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.05;0.95;1" dur="8s" repeatCount="indefinite" />
          </circle>
        </svg>
      </Box>

      {/* Rising thermal bubbles */}
      {bubbles.map((bubble, i) => (
        <Box
          key={i}
          position="absolute"
          left={bubble.left}
          bottom="-50px"
          w={`${bubble.size}px`}
          h={`${bubble.size}px`}
          borderRadius="full"
          bgGradient="radial(circle at 30% 30%, orange.300, red.500)"
          boxShadow="0 0 15px rgba(249, 115, 22, 0.4)"
          animation={`${bubbleFloat} ${bubble.duration}s ease-in-out infinite ${bubble.delay}s`}
        />
      ))}

      {/* Data points - formation markers */}
      {dataPoints.map((point, i) => (
        <Box
          key={i}
          position="absolute"
          left={point.x}
          top={point.y}
          w="10px"
          h="10px"
          borderRadius="full"
          bgGradient="radial(circle, white, orange.400)"
          boxShadow="0 0 12px rgba(249, 115, 22, 0.5), 0 0 24px rgba(220, 38, 38, 0.3)"
          animation={`${dataPointPop} ${2.5 + i * 0.2}s ease-in-out infinite ${point.delay}s`}
        />
      ))}
    </Box>
  );
};

export default HeatZoneBackground;
